import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, RefreshCw, Tractor, Calendar, User, Phone as PhoneIcon,
  AlertCircle, Clock,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';
import {
  getEngageStatusBadge, getEngageSourceBadge,
  ENGAGE_STATUS_LABEL, ENGAGE_SOURCE_LABEL,
} from '../../utils/engageStageBadge';


const CLOSED_STATUSES = new Set(['converted', 'dropped', 'lost']);

const DATE_PRESETS = [
  { value: 'all',       label: 'All' },
  { value: 'overdue',   label: 'Overdue' },
  { value: 'today',     label: 'Today' },
  { value: 'tomorrow',  label: 'Tomorrow' },
  { value: 'this_week', label: 'This week' },
  { value: 'next_7',    label: 'Next 7 days' },
  { value: 'next_30',   label: 'Next 30 days' },
];

const fmt = (iso) => iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—';

// Return day-diff (integer) between date and today (midnight); negative = past
function daysFromToday(iso) {
  if (!iso) return null;
  const target = new Date(iso);
  const today = new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const t1 = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  return Math.round((t1 - t0) / 86400000);
}


export default function FollowupsList() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/enquiries/');
        if (cancelled) return;
        const rows = resp?.results || resp?.data?.results || [];
        // Only open enquiries with a scheduled follow-up datetime. Closing an
        // enquiry (converted/dropped/lost) leaves its last date on the row, so
        // the status check is what keeps finished patients off this list.
        const withFollowup = (Array.isArray(rows) ? rows : [])
          .filter(e => e.followup_datetime && !CLOSED_STATUSES.has(e.status));
        // Sort: overdue first (most-overdue first), then upcoming (soonest first)
        withFollowup.sort((a, b) => new Date(a.followup_datetime) - new Date(b.followup_datetime));
        setEnquiries(withFollowup);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load follow-ups');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const activeSources = useMemo(() => {
    const set = new Set();
    for (const e of enquiries) if (e.source) set.add(e.source);
    return Array.from(set);
  }, [enquiries]);

  const filtered = useMemo(() => enquiries.filter(e => {
    if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const hay = [e.name, e.mobile, e.email, e.message, e.department_of_interest].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (datePreset !== 'all') {
      const d = daysFromToday(e.followup_datetime);
      if (d === null) return false;
      if (datePreset === 'overdue'   && d >= 0) return false;
      if (datePreset === 'today'     && d !== 0) return false;
      if (datePreset === 'tomorrow'  && d !== 1) return false;
      if (datePreset === 'this_week' && (d < 0 || d > 6)) return false;
      if (datePreset === 'next_7'    && (d < 0 || d > 7)) return false;
      if (datePreset === 'next_30'   && (d < 0 || d > 30)) return false;
    }
    return true;
  }), [enquiries, search, datePreset, sourceFilter]);

  // Counts per preset for the chip row
  const counts = useMemo(() => {
    const c = { all: enquiries.length, overdue: 0, today: 0, tomorrow: 0, this_week: 0, next_7: 0, next_30: 0 };
    for (const e of enquiries) {
      const d = daysFromToday(e.followup_datetime);
      if (d === null) continue;
      if (d < 0) c.overdue += 1;
      if (d === 0) c.today += 1;
      if (d === 1) c.tomorrow += 1;
      if (d >= 0 && d <= 6) c.this_week += 1;
      if (d >= 0 && d <= 7) c.next_7 += 1;
      if (d >= 0 && d <= 30) c.next_30 += 1;
    }
    return c;
  }, [enquiries]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-sm">
            <Tractor className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Follow-ups</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} of ${enquiries.length} enquiries with scheduled follow-ups`}
            </p>
          </div>
        </div>
        <button onClick={() => setReloadTick(t => t + 1)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Date preset chips */}
      <div className="flex flex-wrap gap-1.5">
        {DATE_PRESETS.map(p => (
          <button key={p.value} onClick={() => setDatePreset(p.value)}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
              datePreset === p.value
                ? (p.value === 'overdue'
                    ? 'bg-red-600 text-white border-red-700'
                    : 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900')
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
            )}>
            {p.label} <span className="ml-1 opacity-70">{counts[p.value] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search patient, mobile, notes, department…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 text-gray-900 dark:text-white" />
        </div>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
          <option value="all">All sources</option>
          {activeSources.map(s => <option key={s} value={s}>{ENGAGE_SOURCE_LABEL[s] || s}</option>)}
        </select>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-4 py-2.5 font-medium">Patient</th>
                <th className="px-4 py-2.5 font-medium">Mobile</th>
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium">Procedure &amp; quote</th>
                <th className="px-4 py-2.5 font-medium">Stage</th>
                <th className="px-4 py-2.5 font-medium">Follow-up on</th>
                <th className="px-4 py-2.5 font-medium">Recent notes</th>
                <th className="px-4 py-2.5 font-medium">Assigned to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  {enquiries.length ? 'No follow-ups match your filters.' : 'No pending follow-ups. Add a note with a next-follow-up date on any enquiry to see it here.'}
                </td></tr>
              ) : filtered.map(e => {
                const days = daysFromToday(e.followup_datetime);
                const isOverdue = days !== null && days < 0;
                return (
                  <tr key={e.id}
                      onClick={() => navigate(`/engage/enquiries/${e.id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{e.name}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-700 dark:text-gray-300">
                      {e.mobile ? (
                        <span className="inline-flex items-center gap-1"><PhoneIcon className="w-3 h-3 text-gray-400" />{e.mobile}</span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {e.source ? (
                        <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(e.source))}>
                          {ENGAGE_SOURCE_LABEL[e.source] || e.source}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="truncate font-medium text-gray-800 dark:text-gray-200">
                        {e.procedure_of_interest || <span className="text-gray-400 italic font-normal">not set</span>}
                      </div>
                      {e.expected_treatment_value != null && Number(e.expected_treatment_value) > 0 && (
                        <div className="mt-0.5">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold tabular-nums">
                            ₹ {Number(e.expected_treatment_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(e.status))}>
                        {ENGAGE_STATUS_LABEL[e.status] || e.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px]">
                      <div className={cn('inline-flex items-center gap-1', isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-700 dark:text-gray-300')}>
                        {isOverdue ? <AlertCircle className="w-3.5 h-3.5" /> : <Calendar className="w-3 h-3 text-blue-500" />}
                        {fmt(e.followup_datetime)}
                      </div>
                      {days !== null && (
                        <div className={cn('text-[11px] mt-0.5', isOverdue ? 'text-red-500' : 'text-gray-500 dark:text-gray-400')}>
                          {days < 0 ? `${-days}d overdue`
                          : days === 0 ? 'today'
                          : days === 1 ? 'tomorrow'
                          : `in ${days} days`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="text-[12px] text-gray-700 dark:text-gray-300 line-clamp-2">
                        {e.latest_note || e.message || <span className="text-gray-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-gray-700 dark:text-gray-300">
                      {e.assigned_to_name || <span className="inline-flex items-center gap-1 text-gray-400"><Clock className="w-3 h-3" />Unassigned</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
